/**
 * Plain-English explanations of our internals.
 *
 * betlens is used by OPERATOR and AGGREGATOR support, not just ours. They should
 * never need to know our architecture to read a bet. Every internal term in the
 * UI is wrapped in <Explain term="..."> and answers, in one breath:
 * what is this, and what should I conclude from it?
 *
 * Rules for writing these: no jargon defined with more jargon; say what it means
 * for the ticket in front of them; be honest about which sources can lie.
 */

export interface GlossaryEntry {
  title: string;
  body: string;
  /** Optional "so what" line — what to do with this information. */
  hint?: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  // --- identity ------------------------------------------------------------
  bet_id: {
    title: 'Bet ID',
    body: 'The identifier the game gave this bet. Unique within that game.',
  },
  round_id: {
    title: 'Round ID',
    body: 'The game round this bet belongs to. For most games it matches the bet ID. For "roundly" games — where many players bet on one shared event — one round holds many players\' bets, so the two differ.',
  },
  game_uuid: {
    title: 'Game UUID',
    body: 'Permanent identifier of the game. Stable across environments and renames.',
  },
  play_service_id: {
    title: 'Play Service ID',
    body: 'Identifies the business client (operator) this bet belongs to. Every lookup is scoped to it, so you only ever see your own traffic.',
  },
  transaction_id: {
    title: 'Transaction ID',
    body: 'Our internal ID for a single money movement (a bet, win, or rollback). This is the number the game stores against the bet once we confirm it.',
  },
  client_trx_id: {
    title: 'Partner Transaction ID',
    body: "The ID your own system returned when we sent this transaction. If it's empty, we never got a successful confirmation back from you.",
    hint: 'Empty on a win usually means the win never landed on your side.',
  },
  cycle_uniq_id: {
    title: 'Cycle fingerprint',
    body: 'A fingerprint of the whole bet, calculated from the game and bet ID. Every transaction of this bet carries the same one, which is how we stitch the bet together across every system.',
  },
  tr_uniq_id: {
    title: 'Transaction fingerprint',
    body: 'A fingerprint of one single transaction (bet/win/rollback of a given type). Used to guarantee we never write the same transaction twice.',
  },
  session_id: {
    title: 'Session ID',
    body: 'The game session the bet was placed in. One bet can span several sessions in games that keep state — a player can place a bet, close the game, relaunch, and finish the round in a new session.',
  },
  freeround_id: {
    title: 'Freebet reference',
    body: 'Set when the bet was paid for with a freebet/freeround rather than real balance.',
  },

  // --- transaction fields --------------------------------------------------
  action: {
    title: 'Action',
    body: 'What the money movement was: bet (stake taken), win (payout sent), or rollback (a bet reversed).',
  },
  rollbacked: {
    title: 'Rolled back',
    body: 'Whether this transaction was later reversed. A rolled-back bet means the stake was returned to the player.',
  },
  player_ip: {
    title: 'Player IP',
    body: 'The address the bet was placed from. Recorded on the bet only, not on wins.',
  },
  amount_precision: {
    title: 'Amounts',
    body: 'Stored at full precision (18 decimal places) and displayed rounded using this currency\'s rules. Hover any amount to see the exact stored value.',
  },

  // --- the sources (the important ones) ------------------------------------
  source_transactions: {
    title: 'Primary Database',
    body: 'The authoritative record of every money movement. If a transaction exists here, it happened.',
    hint: 'When sources disagree, this one wins.',
  },
  source_bet: {
    title: 'Game bet row',
    body: "The game's own record of the bet, from that game's database. Every game stores different columns, so this section changes shape per game.",
  },
  source_roundDetails: {
    title: 'Round details',
    body: 'What actually happened in the round, reconstructed by the Game Service from the game\u2019s own records — the outcome, and what the player bet on.',
  },
  source_clickhouseRealtime: {
    title: 'Analytics — realtime',
    body: 'A near-instant copy of successful transactions, used for reporting. Deduplicated, so each transaction appears once.',
    hint: 'In the Primary Database but missing here means the copy dropped it — reporting will under-count.',
  },
  source_clickhouseMv: {
    title: 'Analytics — attempt log',
    body: 'A log of every transaction ATTEMPT, including ones that failed. It is not guaranteed complete or correct.',
    hint: 'Use it to see error codes and balances at the time — never to prove a payment happened.',
  },
  source_clickhouseSync: {
    title: 'Analytics — settled',
    body: 'A delayed copy of the Primary Database, roughly an hour behind.',
    hint: 'The most trustworthy of the three copies, but a recent bet legitimately may not be here yet.',
  },
  source_delayDelivery: {
    title: 'Delivery retries',
    body: "When a win or rollback can't reach the operator, it's queued and retried in the background. Rows here show each attempt and its result.",
    hint: 'Rows with delivered = 0 and a high try count are the classic "player did not get paid" case.',
  },
  source_sessions: {
    title: 'Sessions',
    body: 'The game sessions involved in this bet, including the settings the game was launched with.',
  },
  source_freebets: {
    title: 'Freebets',
    body: 'The freebet campaign this bet drew from, with how many of its rounds have been played.',
  },
  source_rtpVariation: {
    title: 'RTP variation',
    body: 'Some games run configurable return-to-player settings. When enabled, this is the exact configuration this bet was played under.',
  },
  source_currencyMetadata: {
    title: 'Currency',
    body: "How this currency is displayed and rounded. It's what turns a raw stored amount into a readable one.",
  },
  source_logs: {
    title: 'Logs',
    body: 'Application log lines mentioning this bet, from every service that touched it.',
  },
  source_traces: {
    title: 'Traces',
    body: 'The request path across services, with timings — useful for finding where a slow or failed request actually broke.',
  },
  combined_round_id: {
    title: 'Combined round ID',
    body: "A game and a bet stuck together — '<game uuid>:<bet id>', or the two joined with nothing between them. Both forms are accepted, so you can paste whichever one a ticket contains.",
    hint: 'It identifies one bet on its own, so entering it fills in both the game and the bet for you.',
  },
  risk_score: {
    title: 'Risk score',
    body: 'A 0–100 heuristic that adds up how many independent things look unusual about this player, and how strongly. It is a triage aid — a reason to look closer — never a finding of fraud or a verdict.',
    hint: 'A high score means "worth a human review", not "guilty". A low score is not a clearance.',
  },
  stake_regularity: {
    title: 'Stake regularity',
    body: 'How many distinct stake amounts the player used across all their bets. A handful of amounts over thousands of bets suggests a script rather than a person.',
  },
  velocity: {
    title: 'Betting velocity',
    body: 'How fast bets are placed — the typical gap between them, and how many landed under a second apart. Machine speed points at automation.',
  },
  shared_ip: {
    title: 'Shared IP',
    body: 'Other accounts in this project seen from the same public IP address as this player — a possible multi-accounting link.',
    hint: 'Only PUBLIC addresses count. A private/proxy address (10.x, 127.x, and similar) is usually the game\u2019s own proxy shared by everyone, so it never raises the score.',
  },
  rollback_rate: {
    title: 'Rollback rate',
    body: 'The share of this player\u2019s bets that were later reversed. A high rate can indicate connection abuse or exploit attempts.',
  },
  freebet_abuse: {
    title: 'Bonus concentration',
    body: 'The share of the player\u2019s bets funded by a freebet rather than real balance. Almost-entirely-bonus play is a classic bonus-abuse pattern.',
  },
  win_concentration: {
    title: 'Payout ratio',
    body: 'How much the player returned relative to what they staked. Winning far above a game\u2019s expected return, over real volume, is worth reviewing for an exploit or arbitrage.',
  },
  activity_hours: {
    title: 'Bets by hour',
    body: 'When in the day (UTC) the player bets. A single narrow spike, or steady round-the-clock play, can indicate automation rather than a person.',
  },
  ggr: {
    title: 'GGR',
    body: 'Gross gaming revenue — what the operator kept: total stakes minus total payouts minus anything rolled back. Negative means players won more than they staked in this period.',
  },
  hold: {
    title: 'Hold',
    body: 'GGR as a share of turnover (stakes minus rollbacks) — the percentage of everything staked that stayed with the operator.',
    hint: 'Over a short period or few rounds it swings wildly; it only settles towards the game\u2019s theoretical margin over volume.',
  },
  partner_feed: {
    title: 'Partner feed',
    body: 'The reconciliation data an operator pulls from us for a period, to match against their own records. This is the same feed, fetched the same way.',
    hint: 'It is built from a copy of the Primary Database that runs about an hour behind, so very recent bets are legitimately not in it yet.',
  },
  delivery_tries: {
    title: 'Tries',
    body: 'How many times we have attempted to deliver this win or rollback to the operator.',
    hint: 'A climbing count with delivered = 0 means the money still has not landed on the operator side.',
  },
  delivery_delivered: {
    title: 'Delivered',
    body: 'Whether the operator finally accepted this transaction.',
    hint: 'A row can carry a "last attempt" timestamp and still be delivered = 0 — that means we gave up, not that it succeeded.',
  },
  delivery_archived: {
    title: 'Archived',
    body: 'The row has been retired from the retry queue; nothing more will be attempted automatically.',
  },
  delivery_last_try_result: {
    title: 'Last result',
    body: "The operator's own response to our most recent attempt — their error code and message, not ours.",
    hint: 'This is the fastest way to tell "our side is broken" from "the operator rejected it".',
  },
  delivery_payload: {
    title: 'Payload',
    body: 'The exact message we are trying to deliver — the bet, amounts, player and session as we recorded them.',
  },
  exchange_rate_updated: {
    title: 'Rate updated',
    body: 'When this particular currency pair was last refreshed. The table is updated per currency, so some pairs are far older than others.',
    hint: 'A stale rate does not break anything, but converted totals in this portal will drift from reality.',
  },
  exchange_rate_invoicing: {
    title: 'Rates vs invoicing',
    body: 'These rates are what this portal uses to display converted figures. Invoicing runs its own conversion, so an invoice total can legitimately differ.',
  },
  token_data: {
    title: 'Session token',
    body: 'The settings snapshot the game was launched with (limits, currency, mode). Stored compressed; shown here decoded.',
  },

  // --- statuses ------------------------------------------------------------
  status_ok: { title: 'OK', body: 'We reached this source and it returned data.' },
  status_empty: {
    title: 'Empty',
    body: 'We reached this source successfully and it genuinely has nothing for this bet.',
    hint: 'This is a real answer — the data is absent, not hidden.',
  },
  status_skipped: {
    title: 'Skipped',
    body: 'Not queried — either not configured in this panel, or not applicable to this bet.',
  },
  status_unavailable: {
    title: 'Unavailable',
    body: 'We could not reach this source. Its data may exist; we just cannot see it right now.',
    hint: 'Do not read this as "no data" — the answer here is unknown.',
  },

  // --- reconciliation ------------------------------------------------------
  reconciliation: {
    title: 'Reconciliation',
    body: 'The same transaction as each system recorded it, side by side. Systems should agree; where they do not, that gap is usually the bug.',
    hint: 'A row in the Primary Database but missing from a copy points at a broken copy, not a missing payment.',
  },
  timezone_skew: {
    title: 'Timestamps',
    body: 'Every timestamp here is shown in one zone (UTC by default — use the toggle for local time) so the order of events is trustworthy. Hover any of them to see what the source literally stored.',
    hint: 'All sources record these in UTC and should agree to the second.',
  },
};

export function explain(term: string): GlossaryEntry | null {
  return GLOSSARY[term] ?? null;
}
